import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, TrendingUp, TrendingDown, Minus, RefreshCw, ChevronDown, Users, X, Phone, Mail, BarChart2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from 'recharts';
import { useSociete } from '../context/SocieteContext';
import * as api from '../api';

// ─── Popover contacts (idem balance âgée) ────────────────────────────────────
function ContactsPopover({ entrepriseId, entrepriseNom }) {
  const [open, setOpen]         = useState(false);
  const [contacts, setContacts] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [pos, setPos]           = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const toggle = async (e) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + 6, left: Math.max(8, rect.left - 620) });
    setOpen(true);
    if (contacts !== null) return;
    setLoading(true);
    try {
      const r = await api.getPalmaresContacts(entrepriseId);
      setContacts(r.data.contacts || []);
    } catch { setContacts([]); }
    finally { setLoading(false); }
  };

  return (
    <>
      <button ref={btnRef} onClick={toggle}
        data-testid={`btn-contacts-palmares-${entrepriseId}`}
        title="Voir les contacts destinataires"
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 22, height: 22, borderRadius: 5, border: '1px solid',
          borderColor: open ? '#93C5FD' : 'var(--border)',
          background: open ? '#EFF6FF' : 'transparent',
          color: open ? '#1D4ED8' : '#9CA3AF',
          cursor: 'pointer', flexShrink: 0, transition: 'all .15s',
        }}>
        <Users size={12} />
      </button>

      {open && (
        <div ref={popRef}
          data-testid={`popover-contacts-palmares-${entrepriseId}`}
          style={{
            position:'fixed', top:pos.top, left:pos.left, zIndex:9999,
            width:1260, background:'#fff', border:'1px solid var(--border)',
            borderRadius:10, boxShadow:'0 8px 32px rgba(0,0,0,0.12)', overflow:'hidden',
          }}
          onClick={e => e.stopPropagation()}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
            padding:'10px 14px', borderBottom:'1px solid var(--border)', background:'#F8FAFC' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <Users size={14} style={{ color:'#1D4ED8' }} />
              <span style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>
                Contacts — {entrepriseNom}
              </span>
            </div>
            <button onClick={() => setOpen(false)}
              style={{ border:'none', background:'none', cursor:'pointer', color:'#6B7280', display:'flex', padding:2 }}>
              <X size={14} />
            </button>
          </div>
          <div>
            {loading ? (
              <div style={{ padding:'20px 14px', textAlign:'center', fontSize:13, color:'#9CA3AF' }}>Chargement…</div>
            ) : !contacts || contacts.length === 0 ? (
              <div style={{ padding:'20px 14px', textAlign:'center', fontSize:13, color:'#9CA3AF' }}>
                Aucun contact enregistré pour cette entreprise
              </div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, tableLayout:'fixed' }}>
                <colgroup>
                  <col style={{ width:120 }}/><col style={{ width:150 }}/><col style={{ width:165 }}/>
                  <col style={{ width:195 }}/><col style={{ width:165 }}/><col style={{ width:165 }}/><col style={{ width:300 }}/>
                </colgroup>
                <thead>
                  <tr style={{ background:'#F1F5F9' }}>
                    {['Prénom','Nom','Fonction','Communication','Ligne directe','Portable','Email'].map(h => (
                      <th key={h} style={{ padding:'6px 10px', textAlign:'left', fontWeight:600,
                        color:'#475569', borderBottom:'1px solid #E2E8F0', whiteSpace:'nowrap',
                        overflow:'hidden', textOverflow:'ellipsis' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c, i) => (
                    <tr key={c.personne_id} style={{ background: i%2===0 ? '#fff' : '#F8FAFC', borderBottom:'1px solid #F1F5F9' }}>
                      <td style={{ padding:'6px 10px', color:'#374151', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{c.prenom||'—'}</td>
                      <td style={{ padding:'6px 10px', fontWeight:500, color:'#111827', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{c.nom||'—'}</td>
                      <td style={{ padding:'6px 10px', color:'#6B7280', fontStyle:'italic', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{c.fonction||'—'}</td>
                      <td style={{ padding:'6px 10px' }}>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:3 }}>
                          {(c.automation_tags || []).length > 0
                            ? (c.automation_tags || []).map((t, ti) => (
                                <span key={ti} style={{
                                  fontSize:10, padding:'1px 6px', borderRadius:9999,
                                  background:(t.couleur||'#6B7280')+'22', color:t.couleur||'#6B7280',
                                  border:`1px solid ${(t.couleur||'#6B7280')}55`, whiteSpace:'nowrap',
                                }}>{t.nom}</span>
                              ))
                            : <span style={{ color:'#D1D5DB' }}>—</span>
                          }
                        </div>
                      </td>
                      <td style={{ padding:'6px 10px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {c.ligne_directe ? <a href={`tel:${c.ligne_directe}`} style={{ color:'#1D4ED8', textDecoration:'none', display:'flex', alignItems:'center', gap:4 }} onClick={e=>e.stopPropagation()}><Phone size={10}/><span>{c.ligne_directe}</span></a> : <span style={{ color:'#D1D5DB' }}>—</span>}
                      </td>
                      <td style={{ padding:'6px 10px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {c.telephone_mobile ? <a href={`tel:${c.telephone_mobile}`} style={{ color:'#1D4ED8', textDecoration:'none', display:'flex', alignItems:'center', gap:4 }} onClick={e=>e.stopPropagation()}><Phone size={10}/><span>{c.telephone_mobile}</span></a> : <span style={{ color:'#D1D5DB' }}>—</span>}
                      </td>
                      <td style={{ padding:'6px 10px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {c.email ? <a href={`mailto:${c.email}`} style={{ color:'#1D4ED8', textDecoration:'none', display:'flex', alignItems:'center', gap:4 }} onClick={e=>e.stopPropagation()}><Mail size={10}/><span>{c.email}</span></a> : <span style={{ color:'#D1D5DB' }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Popover graphique CA mensuel ────────────────────────────────────────────
const TRIMESTRES = ['T1','T1','T1','T2','T2','T2','T3','T3','T3','T4','T4','T4'];

function CaPopover({ entrepriseId, entrepriseNom, year }) {
  const [open, setOpen]   = useState(false);
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [pos, setPos]     = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open]);

  const toggle = async (e) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) {
      const left = Math.min(rect.left, window.innerWidth - 480);
      const top  = rect.bottom + 8;
      setPos({ top, left: Math.max(8, left) });
    }
    setOpen(true);
    if (data !== null) return;
    setLoading(true);
    try {
      const r = await api.getPalmaresClientMensuel(entrepriseId, { year });
      setData(r.data.mois || []);
    } catch { setData([]); }
    finally { setLoading(false); }
  };

  const fmtK = (v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : `${Math.round(v)}`;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const entry = (data || []).find(d => d.mois_num === label);
    const moisNom = entry?.mois || label;
    return (
      <div style={{ background:'#fff', border:'1px solid #E2E8F0', borderRadius:8, padding:'8px 12px', fontSize:12, boxShadow:'0 4px 12px rgba(0,0,0,0.1)' }}>
        <p style={{ fontWeight:600, color:'#374151', marginBottom:4 }}>{moisNom}</p>
        {payload.map(p => (
          <p key={p.dataKey} style={{ color: p.color, margin:'2px 0' }}>
            {p.dataKey === 'ca_n' ? year : year - 1} : <strong>{fmtEur(p.value)}</strong>
          </p>
        ))}
      </div>
    );
  };

  return (
    <>
      <button ref={btnRef} onClick={toggle}
        data-testid={`btn-ca-mensuel-${entrepriseId}`}
        title="Voir l'évolution mensuelle du CA"
        style={{
          display:'inline-flex', alignItems:'center', justifyContent:'center',
          width:22, height:22, borderRadius:5, border:'1px solid',
          borderColor: open ? '#93C5FD' : 'var(--border)',
          background: open ? '#EFF6FF' : 'transparent',
          color: open ? '#1D4ED8' : '#9CA3AF',
          cursor:'pointer', flexShrink:0, transition:'all .15s',
        }}>
        <BarChart2 size={12} />
      </button>

      {open && (
        <div ref={popRef}
          data-testid={`popover-ca-mensuel-${entrepriseId}`}
          style={{
            position:'fixed', top:pos.top, left:pos.left, zIndex:9999,
            width:460, background:'#fff', border:'1px solid var(--border)',
            borderRadius:12, boxShadow:'0 8px 32px rgba(0,0,0,0.13)', overflow:'hidden',
          }}
          onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
            padding:'10px 14px', borderBottom:'1px solid var(--border)', background:'#F8FAFC' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <BarChart2 size={14} style={{ color:'#1D4ED8' }} />
              <span style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>
                CA mensuel HT — {entrepriseNom}
              </span>
            </div>
            <button onClick={() => setOpen(false)}
              style={{ border:'none', background:'none', cursor:'pointer', color:'#6B7280', display:'flex', padding:2 }}>
              <X size={14} />
            </button>
          </div>
          {/* Graphique */}
          <div style={{ padding:'16px 14px 10px' }}>
            {loading ? (
              <div style={{ height:180, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, color:'#9CA3AF' }}>Chargement…</div>
            ) : !data || data.every(d => d.ca_n === 0 && d.ca_n1 === 0) ? (
              <div style={{ height:180, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, color:'#9CA3AF' }}>Aucune donnée</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={data} margin={{ top:4, right:8, bottom:4, left:0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis
                    dataKey="mois_num"
                    tickFormatter={(v) => TRIMESTRES[v - 1]}
                    ticks={[1, 4, 7, 10]}
                    tick={{ fontSize: 11, fill: '#6B7280' }}
                    tickLine={false}
                    axisLine={{ stroke: '#E2E8F0' }}
                  />
                  <YAxis
                    tickFormatter={fmtK}
                    tick={{ fontSize: 10, fill: '#9CA3AF' }}
                    tickLine={false}
                    axisLine={false}
                    width={36}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    formatter={(v) => v === 'ca_n' ? `${year}` : `${year - 1}`}
                    iconType="circle" iconSize={8}
                    wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                  />
                  <Line type="monotone" dataKey="ca_n"  stroke="#2563EB" strokeWidth={2} dot={false} name="ca_n"  activeDot={{ r:4 }} />
                  <Line type="monotone" dataKey="ca_n1" stroke="#94A3B8" strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="ca_n1" activeDot={{ r:3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function fmtEur(n) {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)} M€`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)} k€`;
  return `${Math.round(n).toLocaleString('fr-FR')} €`;
}

function VariationBadge({ pct }) {
  if (pct === null || pct === undefined) {
    return <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#F0FDF4', color: '#15803D' }}>Nouveau</span>;
  }
  if (pct > 0) return (
    <span className="inline-flex items-center gap-0.5 text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#F0FDF4', color: '#15803D' }}>
      <TrendingUp size={11} />{`+${pct}%`}
    </span>
  );
  if (pct < 0) return (
    <span className="inline-flex items-center gap-0.5 text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#FFF1F2', color: '#BE123C' }}>
      <TrendingDown size={11} />{`${pct}%`}
    </span>
  );
  return (
    <span className="inline-flex items-center gap-0.5 text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#F8FAFC', color: '#64748B' }}>
      <Minus size={11} />0%
    </span>
  );
}

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2, CURRENT_YEAR - 3];

export default function PalmaresClients() {
  const navigate = useNavigate();
  const { activeAgences } = useSociete();
  const [year, setYear] = useState(CURRENT_YEAR);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getPalmaresClients({ year });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load, activeAgences]);

  const clients = data?.clients || [];
  const top80Count = clients.filter(c => c.top80).length;
  const totalClients = clients.length;

  // Trouver la ligne de bascule 80%
  const pareto80Index = clients.findIndex(c => !c.top80);

  return (
    <div className="flex-1 overflow-auto p-6 space-y-6" style={{ background: 'var(--bg)' }}>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--text)' }}>
            <Trophy size={22} className="text-amber-500" />
            Palmarès clients
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Chiffre d'affaires HT — Factures validées
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Sélecteur année */}
          <div className="relative">
            <select
              data-testid="palmares-year-select"
              value={year}
              onChange={e => setYear(Number(e.target.value))}
              className="appearance-none pl-3 pr-8 py-2 rounded-xl text-sm font-medium border"
              style={{ background: 'var(--bg-subtle)', borderColor: 'var(--border)', color: 'var(--text)', outline: 'none' }}
            >
              {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-muted)' }} />
          </div>
          <button
            data-testid="palmares-refresh-btn"
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all"
            style={{ background: 'var(--bg-subtle)', borderColor: 'var(--border)', color: 'var(--text)' }}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
        </div>
      </div>

      {/* KPIs */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl p-4 border" style={{ borderColor: 'var(--border)' }}>
            <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>CA Total {year}</p>
            <p className="text-xl font-bold" style={{ color: 'var(--text)' }}>{fmtEur(data.total_n)}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 border" style={{ borderColor: 'var(--border)' }}>
            <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Clients actifs</p>
            <p className="text-xl font-bold" style={{ color: 'var(--text)' }}>{totalClients}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 border" style={{ borderColor: 'var(--border)' }}>
            <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Top 80% du CA</p>
            <p className="text-xl font-bold" style={{ color: 'var(--primary-light)' }}>{top80Count} clients</p>
          </div>
          <div className="bg-white rounded-2xl p-4 border" style={{ borderColor: 'var(--border)' }}>
            <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Reste (20%)</p>
            <p className="text-xl font-bold" style={{ color: 'var(--text-muted)' }}>{totalClients - top80Count} clients</p>
          </div>
        </div>
      )}

      {/* État vide / erreur */}
      {error && (
        <div className="bg-white rounded-2xl p-8 text-center border" style={{ borderColor: 'var(--border)' }}>
          <p className="text-sm" style={{ color: '#EF4444' }}>{error}</p>
        </div>
      )}

      {/* Tableau */}
      {!error && (
        <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <table className="w-full text-sm" style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 48 }} />
              <col />
              <col style={{ width: 140 }} />
              <col style={{ width: 36 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 100 }} />
            </colgroup>
            <thead>
              <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                <th className="px-4 py-3 text-left font-semibold text-xs" style={{ color: 'var(--text-muted)' }}>#</th>
                <th className="px-4 py-3 text-left font-semibold text-xs" style={{ color: 'var(--text-muted)' }}>Client</th>
                <th className="px-4 py-3 text-center font-semibold text-xs" style={{ color: 'var(--text-muted)' }}>CA {year}</th>
                <th></th>
                <th className="px-4 py-3 text-center font-semibold text-xs" style={{ color: 'var(--text-muted)' }}>CA {year - 1}</th>
                <th className="px-4 py-3 text-center font-semibold text-xs" style={{ color: 'var(--text-muted)' }}>Variation</th>
                <th className="px-4 py-3 text-right font-semibold text-xs" style={{ color: 'var(--text-muted)' }}>Cumul</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="text-center py-16 text-sm" style={{ color: 'var(--text-placeholder)' }}>Chargement…</td></tr>
              )}
              {!loading && clients.length === 0 && (
                <tr><td colSpan={6} className="text-center py-16 text-sm" style={{ color: 'var(--text-placeholder)' }}>
                  <Users size={32} className="mx-auto mb-2 opacity-30" />
                  Aucune facture trouvée pour {year}
                </td></tr>
              )}
              {!loading && clients.map((c, idx) => {
                const isParetoBoundary = idx === pareto80Index && pareto80Index > 0;
                return (
                  <React.Fragment key={c.entreprise_id || idx}>
                    {isParetoBoundary && (
                      <tr>
                        <td colSpan={6} style={{ padding: 0 }}>
                          <div className="flex items-center gap-2 px-4 py-1.5 text-xs font-semibold"
                               style={{ background: '#FEF9C3', color: '#854D0E', borderTop: '2px dashed #FDE047', borderBottom: '1px solid #FDE047' }}>
                            <span>— 80% du CA atteint — Les {totalClients - top80Count} clients suivants représentent 20% restants —</span>
                          </div>
                        </td>
                      </tr>
                    )}
                    <tr
                      data-testid={`palmares-row-${c.rang}`}
                      style={{
                        borderBottom: '1px solid var(--border)',
                        background: idx % 2 === 0 ? 'white' : '#FAFBFC',
                      }}
                    >
                      <td className="px-4 py-3">
                        <span className="text-xs font-bold tabular-nums" style={{ color: c.rang <= 3 ? '#F59E0B' : 'var(--text-muted)' }}>
                          {c.rang <= 3 ? ['🥇','🥈','🥉'][c.rang - 1] : `#${c.rang}`}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium" style={{ color: 'var(--text)' }}>
                        <div className="flex items-center gap-2">
                          <span
                            onClick={() => c.entreprise_id && navigate(`/annuaire?open_ent=${c.entreprise_id}`)}
                            style={{ cursor: c.entreprise_id ? 'pointer' : 'default' }}
                            className="hover:underline"
                          >
                            {c.entreprise_nom}
                          </span>
                          {c.entreprise_id && (
                            <ContactsPopover
                              entrepriseId={c.entreprise_id}
                              entrepriseNom={c.entreprise_nom}
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center font-semibold tabular-nums" style={{ color: 'var(--text)' }}>
                        {fmtEur(c.ca_n)}
                      </td>
                      <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        {c.entreprise_id && c.ca_n > 0 && (
                          <CaPopover entrepriseId={c.entreprise_id} entrepriseNom={c.entreprise_nom} year={year} />
                        )}
                      </td>
                      <td className="px-4 py-3 text-center tabular-nums" style={{ color: 'var(--text-muted)' }}>
                        {c.ca_n1 > 0 ? fmtEur(c.ca_n1) : <span style={{ opacity: 0.3 }}>—</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <VariationBadge pct={c.variation_pct} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                            <div className="h-full rounded-full transition-all"
                                 style={{ width: `${Math.min(c.cumul_pct, 100)}%`, background: 'var(--primary-light)' }} />
                          </div>
                          <span className="text-xs tabular-nums font-medium" style={{ color: 'var(--primary-light)', minWidth: 38 }}>
                            {c.cumul_pct}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
